import React, { useCallback, useEffect, useMemo, useState } from 'react';
import 'firebase/firestore';
import { makeStyles } from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import { Plan } from './types';
import PlanEditor from './PlanEditor';
import { deletePlan, savePlan, watchPlans } from './firestore';
import { hasMapsKey } from './google';

const useStyles = makeStyles(() => ({
  page: {
    minHeight: '100vh',
    background: '#f4eef9',
    paddingBottom: 'env(safe-area-inset-bottom, 2em)',
  },
  warning: {
    background: '#fff3cd',
    color: '#5b3a00',
    padding: '0.5em 1em',
    fontSize: '0.85em',
    textAlign: 'center',
  },
  notice: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '1.5em 1em',
    textAlign: 'center',
    color: '#666',
  },
}));

function makePlanId() {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function newPlan(): Plan {
  const now = new Date();
  now.setMinutes(0, 0, 0);
  now.setHours(now.getHours() + 2);
  return {
    id: makePlanId(),
    name: 'New plan',
    appointmentTime: now.toISOString(),
    arriveEarlyMinutes: 10,
    steps: [],
    updatedAt: Date.now(),
  };
}

interface RouteParams {
  planId?: string;
}

export default function Depart() {
  const classes = useStyles();
  const history = useHistory();
  const { planId } = useParams<RouteParams>();
  const [plans, setPlans] = useState<Plan[]>([]);
  const [draftPlan, setDraftPlan] = useState<Plan>(() => newPlan());
  const [loading, setLoading] = useState(true);
  const [dbError, setDbError] = useState<string | null>(null);
  const [pendingSavedPlan, setPendingSavedPlan] = useState<Plan | null>(null);

  useEffect(() => {
    const unsub = watchPlans(
      list => { setPlans(list); setLoading(false); setDbError(null); },
      err => { setDbError(err.message); setLoading(false); },
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (pendingSavedPlan && plans.some(plan => plan.id === pendingSavedPlan.id)) {
      setPendingSavedPlan(null);
    }
  }, [pendingSavedPlan, plans]);

  const handleCreate = useCallback(() => {
    setDraftPlan(newPlan());
    history.push('/depart');
  }, [history]);

  const handleDelete = useCallback(async (deletePlanId: string) => {
    if (!window.confirm('Delete this plan?')) return;
    await deletePlan(deletePlanId);
    setPendingSavedPlan(current => current && current.id === deletePlanId ? null : current);
    if (planId === deletePlanId) {
      setDraftPlan(newPlan());
      history.push('/depart');
    }
  }, [history, planId]);

  const handleOpen = useCallback((nextPlanId: string) => {
    history.push(`/depart/${nextPlanId}`);
  }, [history]);

  const handleSave = useCallback(async (plan: Plan) => {
    const savedPlan = await savePlan(plan);
    setPendingSavedPlan(savedPlan);
    if (history.location.pathname !== `/depart/${savedPlan.id}`) {
      history.replace(`/depart/${savedPlan.id}`);
    }
    return savedPlan;
  }, [history]);

  const routePlan = useMemo(() => {
    if (!planId) return null;
    return (pendingSavedPlan && pendingSavedPlan.id === planId ? pendingSavedPlan : null)
      || plans.find(p => p.id === planId)
      || null;
  }, [pendingSavedPlan, planId, plans]);

  const editorPlan = planId ? routePlan : draftPlan;
  const isPersisted = Boolean(planId && routePlan);

  return (
    <div className={classes.page}>
      {dbError && <div className={classes.warning}>Firestore error: {dbError}</div>}
      {!hasMapsKey() && (
        <div className={classes.warning}>
          Google Maps key not configured — driving lookup is disabled. Set <code>REACT_APP_GOOGLE_MAPS_API_KEY</code> in <code>.env</code>.
        </div>
      )}
      {planId && !routePlan ? (
        loading ? (
          <div className={classes.notice}>Loading plan…</div>
        ) : (
          <div className={classes.notice}>Plan not found.</div>
        )
      ) : editorPlan ? (
        <PlanEditor
          plan={editorPlan}
          isPersisted={isPersisted}
          onSave={handleSave}
          plans={plans}
          plansLoading={loading}
          onOpenPlan={handleOpen}
          onCreatePlan={handleCreate}
          onDeletePlan={handleDelete}
        />
      ) : null}
    </div>
  );
}
